import React from 'react';
import { useFormContext } from 'react-hook-form';
import { showToastMessage } from '../../../utils/commonFunctions';

// Renders a checkbox field with custom toggle styling
export const CheckboxField = React.memo(({ name, label }: { name: string; label: string }) => {
  const { register, watch, setValue } = useFormContext();
  const isChecked = watch(name);

  return (
    <div className="df-field col-12">
      <div className="toggle-container">
        <label className="df-label">{label}</label>
        <div className="toggle-wrapper">
          <input
            type="checkbox"
            className="hidden-checkbox"
            id={name}
            checked={!!isChecked}
            onChange={(e) => setValue(name, e.target.checked)}
          />
          <label
            htmlFor={name}
            className={`custom-toggle ${isChecked ? 'active' : ''}`}
          >
            <div className="toggle-handle"></div>
          </label>
        </div>
      </div>
      <input type="hidden" {...register(name)} />
    </div>
  );
});

CheckboxField.displayName = 'CheckboxField';

// Renders a number input field with min/max validation, unit display, and float precision control
export const NumberField = React.memo(({ name, label, min, max, required, rules, unit, type, mergeRegisterOnChange = false }:
  { name: string; label: string; min?: number; max?: number; required?: boolean; rules: any; unit?: string; type?: string; mergeRegisterOnChange?: boolean }) => {
  const { register, formState: { errors }, setValue, trigger } = useFormContext();
  const registration = register(name, { valueAsNumber: true, ...rules });
  const { onChange: regOnChange, ...registerRest } = registration;

  // Handles number input changes and enforces max 8 decimal places for float types
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (mergeRegisterOnChange) {
      regOnChange(e);
    }
    let value = e.target.value;

    if (type === 'float' && value) {
      const decimalIndex = value.indexOf('.');
      if (decimalIndex !== -1 && value.length - decimalIndex > 9) {
        value = parseFloat(value).toFixed(8);
        setValue(name, value);
        trigger(name);
        showToastMessage('info', 'Maximum 8 decimal places allowed');
      }
    }
  };

  return (
    <div className="df-field">
      <label className="df-label" htmlFor={name}>
        {label}
        {required && <span className="required-asterisk">*</span>}
      </label>
      <div className="dynamic-form-unit-container">
        <input
          id={name}
          type="number"
          step={type === 'float' ? "0.00000001" : "any"}
          {...(mergeRegisterOnChange ? registerRest : registration)}
          min={min}
          max={max}
          className={`dynamic-form-unit-input ${errors[name] ? 'error' : ''}`}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              e.preventDefault();
            }
          }}
          onWheel={(e) => {
            // Prevent scroll from changing the value
            e.currentTarget.blur();
          }}
          onChange={handleNumberChange}
        />
        {unit && <span className="dynamic-form-unit-label">
          {unit}
        </span>}
      </div>
      {errors[name]?.message && (
        <p className="error-message">{String(errors[name]?.message)}</p>
      )}
    </div>
  );
});

NumberField.displayName = 'NumberField';

// Renders a text input field with validation
export const TextField = React.memo(({ name, label, required, rules }:
  { name: string; label: string; required?: boolean; rules: any }) => {
  const { register, formState: { errors } } = useFormContext();

  return (
    <div className="df-field">
      <label className="df-label" htmlFor={name}>
        {label}
        {required && <span className="required-asterisk">*</span>}
      </label>
      <input
        id={name}
        className={`df-input ${errors[name] ? 'error' : ''}`}
        type="text"
        {...register(name, rules)}
      />
      {errors[name]?.message && (
        <p className="error-message">{String(errors[name]?.message)}</p>
      )}
    </div>
  );
});

TextField.displayName = 'TextField';
